import React, { useEffect, useState } from "react";
import {
  Box, Typography, Card, Stack, TextField, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper,
  CircularProgress, Divider, Button, Grid
} from "@mui/material";
import { CalendarMonth, Print, Call, LocationOn } from "@mui/icons-material";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { db, auth } from "../../firebase";

function Daybook() {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [schoolId, setSchoolId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Balances
  const [openingBalance, setOpeningBalance] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);

  // Lists
  const [feeIncomes, setFeeIncomes] = useState([]);
  const [otherIncomes, setOtherIncomes] = useState([]);
  const [inventoryIncomes, setInventoryIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expenses1, setExpenses1] = useState([]);
  const [walletIncomes, setWalletIncomes] = useState([]);

  // Summary Totals
  const [dayTotalIncome, setDayTotalIncome] = useState(0);
  const [bankingRecords, setBankingRecords] = useState([]); // State for dailydaybank records
  const [dailyNotes, setdailyNotes] = useState([]); // State for dailydaybank records
  const [dayTotalExpense, setDayTotalExpense] = useState(0);

  useEffect(() => {
    getSchoolId();
  }, []);

  useEffect(() => {
    if (schoolId && selectedDate) {
      fetchDaybookData();
    }
  }, [schoolId, selectedDate]);

  const getSchoolId = async () => {
    try {
      const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
      setSchoolId(userSnap.data()?.schoolId || null);
    } catch (e) {
      console.error("Initialization error:", e);
      setLoading(false);
    }
  };

  const fetchDaybookData = async () => {
    setLoading(true);
    try {
      const BASE_DATE = "2026-07-01";
      const INITIAL_OPENING_BASE = 5089.00;

      const feeStructureQ = query(collection(db, "fee_structures"));
      const historicalIncomeQ = query(collection(db, "income"));
      const historicalExpenseQ = query(collection(db, "expenses"), where('dayBook', '==', 'Yes'));
      const historicalExpenseQ1 = query(collection(db, "expenses"), where('dayBook', '==', 'No'));
      const inventorySalesQ = query(collection(db, "studentInventory"), where('payMode', '!=', 'Wallet'));
      const walletTransactionsQ = query(collection(db, "wallet_transactions"));
      const dailyDayBankQ = query(collection(db, "dailydaybank"), where("schoolId", "==", schoolId), where("date", "==", selectedDate));
      const dailybookNoteQ = query(collection(db, "daybooknotes"), where("schoolId", "==", schoolId), where("date", "==", selectedDate));


      const [
        feeStructuresSnap,
        directIncomeSnap,
        expenseSnap,
        expenseSnap1,
        inventorySalesSnap,
        walletSnap,
        dailyDayBankSnap,
        dailyNoteSnap,
      ] = await Promise.all([
        getDocs(feeStructureQ),
        getDocs(historicalIncomeQ),
        getDocs(historicalExpenseQ),
        getDocs(historicalExpenseQ1),
        getDocs(inventorySalesQ),
        getDocs(walletTransactionsQ),
        getDocs(dailyDayBankQ),
        getDocs(dailybookNoteQ)

      ]);

      // --- 3. CALCULATE CUMULATIVE HISTORICAL BALANCE (EXCLUDING SELECTED DATE) ---
      let midPeriodIncome = 0;
      let midPeriodExpense = 0;

      if (selectedDate > BASE_DATE) {
        // Historical Fee payments
        feeStructuresSnap.docs.forEach(docSnap => {
          const history = docSnap.data().paymentHistory || [];
          history.forEach(p => {
            if (p.date && p.date >= BASE_DATE && p.date < selectedDate) {
              midPeriodIncome += parseFloat(p.paidAmount || 0);
            }
          });
        });

        // Historical Direct incomes
        directIncomeSnap.docs.forEach(docSnap => {
          const d = docSnap.data();
          if (d.date && d.date >= BASE_DATE && d.date < selectedDate) {
            midPeriodIncome += parseFloat(d.amount || 0);
          }
        });

        // Historical Student Inventory Sales
        inventorySalesSnap.docs.forEach(docSnap => {
          const d = docSnap.data();
          if (d.issueDate && d.issueDate >= BASE_DATE && d.issueDate < selectedDate) {
            midPeriodIncome += parseFloat(d.grandTotal || 0);
          }
        });

        // Historical Wallet Deposits/Topups
        walletSnap.docs.forEach(docSnap => {
          const d = docSnap.data();
          const tDate = d.dateTime;
          if (tDate && tDate >= BASE_DATE && tDate < selectedDate) {
            if (d.type === 'Credit' || d.type === 'deposit') {
              midPeriodIncome += parseFloat(d.amount || 0);
            }
          }
        });

        // Historical Expenses (Only include dayBook == 'Yes')
        expenseSnap.docs.forEach(docSnap => {
          const d = docSnap.data();
          if (d.date && d.date >= BASE_DATE && d.date < selectedDate) {
            midPeriodExpense += parseFloat(d.amount || 0);
          }
        });
      }

      // Compute rolling live liquidity limits (Opening balance for selected date)
      let calculatedOpening = INITIAL_OPENING_BASE;
      if (selectedDate > BASE_DATE) {
        calculatedOpening = INITIAL_OPENING_BASE + midPeriodIncome - midPeriodExpense;
      }
      setOpeningBalance(calculatedOpening);

      // --- 4. FETCH ACTIVE SELECTED DAY TRANSACTIONS ---
      const currentDayFees = [];
      feeStructuresSnap.docs.forEach(docSnap => {
        const studentData = docSnap.data();
        const history = studentData.paymentHistory || [];
        history.forEach(payment => {
          if (payment.date === selectedDate) {
            currentDayFees.push({
              id: `${docSnap.id}_${payment.timestamp || Math.random()}`,
              name: studentData.fullName || "Student Fee Payment",
              desc: payment.note ? `Note: ${payment.note}` : "School Fee Collection",
              mode: payment.payMode || "Cash",
              amount: parseFloat(payment.paidAmount || 0),
              cat: "Fee Payment"
            });
          }
        });
      });

      const currentDayIncomes = [];
      directIncomeSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (data.date === selectedDate) {
          currentDayIncomes.push({
            id: docSnap.id,
            name: data.personName || "Other Source",
            desc: data.description || "Direct Inward Income",
            mode: data.paymentMethod || "Direct Transfer",
            amount: parseFloat(data.amount || 0),
            cat: "Other Payments"
          });
        }
      });

      const currentDayInventory = [];
      inventorySalesSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (data.issueDate === selectedDate) {
          currentDayInventory.push({
            id: docSnap.id,
            name: data.studentName || "Inventory Assignment",
            desc: `Issued ${data.items?.length || 0} items to student`,
            mode: data.payMode || data.paymentSource || "Direct",
            amount: parseFloat(data.grandTotal || 0),
            cat: "Inventory"
          });
        }
      });

      // Handle Current Day Wallet Influxes
      const currentDayWallet = [];
      walletSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        const tDate = data.dateTime;
        if (tDate === selectedDate && (data.type === 'deposit' || data.type === 'Credit')) {
          currentDayWallet.push({
            id: docSnap.id,
            name: data.studentName || "Student Wallet",
            desc: data.description || "Wallet Topup Deposit",
            mode: data.payMode || "Wallet/Cash",
            amount: parseFloat(data.amount || 0),
            cat: "Wallet Deposit"
          });
        }
      });

      const currentDayExpenses = [];
      expenseSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (data.date === selectedDate) {
          currentDayExpenses.push({
            id: docSnap.id,
            name: data.personName || "Vendor/Service",
            desc: data.description || "Debit Outflow Entry",
            amount: parseFloat(data.amount || 0),
            payMode: data.payMode || "Cash"
          });
        }
      });

      const currentDayExpenses1 = [];
      expenseSnap1.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (data.date === selectedDate) {
          currentDayExpenses1.push({
            id: docSnap.id,
            name: data.personName || "Vendor/Service",
            desc: data.description || "Debit Outflow Entry",
            amount: parseFloat(data.amount || 0),
            payMode: data.payMode || "Online"
          });
        }
      });

      // Fetch, clear, and map structured dynamic data from dailydaybank collection
      const currentBankingRows = [];
      dailyDayBankSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        currentBankingRows.push({
          id: docSnap.id,
          fieldName: data.fieldName || "N/A",
          fieldType: data.fieldType || "Debit/Credit",
          amount: parseFloat(data.amount || 0)
        });
      });

      const currentNotesRows = [];
      dailyNoteSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        currentNotesRows.push({
          id: docSnap.id,
          description: data.note,
        });
      });

      setFeeIncomes(currentDayFees);
      setOtherIncomes(currentDayIncomes);
      setInventoryIncomes(currentDayInventory);
      setWalletIncomes(currentDayWallet);
      setExpenses(currentDayExpenses);
      setExpenses1(currentDayExpenses1);
      setBankingRecords(currentBankingRows);
      setdailyNotes(currentNotesRows)

      // --- 5. MATH SUMMARY CALCULATIONS ---
      let totalDayInc = 0;
      currentDayFees.forEach(f => totalDayInc += f.amount);
      currentDayIncomes.forEach(i => totalDayInc += i.amount);
      currentDayInventory.forEach(inv => totalDayInc += inv.amount);
      currentDayWallet.forEach(w => totalDayInc += w.amount);

      let totalDayExp = 0;
      currentDayExpenses.forEach(e => totalDayExp += e.amount);

      setDayTotalIncome(totalDayInc);
      setDayTotalExpense(totalDayExp);
      setClosingBalance(calculatedOpening + totalDayInc - totalDayExp);

    } catch (e) {
      console.error("Error executing daybook balances tracking calculation:", e);
    } finally {
      setLoading(false); // Fix: Force loading to false reliably
    }
  };

  // Safe compilation of income structures
  const allIncomes = [...feeIncomes, ...otherIncomes, ...inventoryIncomes, ...walletIncomes];

  // Fix: Handles printing cleanly by executing safely outside the main thread execution block
  const handlePrint = () => {
    if (loading) return;
    setTimeout(() => {
      window.print();
    }, 300);
  };

  return (
    <Box
      p={1}
      bgcolor="#f4f6f9"
      minHeight="100vh"
      sx={{
        "@media print": {
          bgcolor: "#fff",
          p: 1,
          m: 0,
          width: "100% !important",
          minWidth: "100% !important",
          "@page": { margin: "1mm", size: "auto" },
          ".no-print": { display: "none !important" },
          ".print-area": {
            p: 1,
            m: 0,
            width: "100% !important",
            maxWidth: "100% !important",
            boxShadow: "none !important",
            border: "none !important",
            display: "block !important"
          }
        }
      }}
    >
      {/* Top Control Panel */}
      <Stack className="no-print" direction="row" justifyContent="space-between" alignItems="center" mb={2} px={1}>
        <Box>
          <Typography variant="h6" fontWeight={800} color="text.primary">Daybook Ledger Statements</Typography>
          <Typography variant="caption" color="text.secondary">Compile institutional accounting summaries</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <TextField
            type="date"
            size="small"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputProps={{
              startAdornment: <CalendarMonth sx={{ fontSize: 16, mr: 0.5, color: "text.secondary" }} />,
              sx: { borderRadius: 2, height: 34, fontSize: '12.5px', bgcolor: '#fff' }
            }}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<Print />}
            disabled={loading}
            onClick={handlePrint}
            sx={{ borderRadius: 2, height: 34, fontSize: '12.5px', textTransform: 'none', px: 2, fontWeight: 700, boxShadow: 'none' }}
          >
            Print Report
          </Button>
        </Stack>
      </Stack>

      {/* Main Ledger Sheet */}
      <Card className="print-area" sx={{ p: 3, maxWidth: 850, mx: 'auto', borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.03)", border: "1px solid #e2e8f0", bgcolor: "#fff" }}>

        {/* BRANDING HEADER BLOCK */}
        <Box sx={{ borderBottom: '2px solid #0f172a', pb: 2, mb: 2.5 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item xs={8}>
              <Typography variant="h5" fontWeight={900} color="text.primary" sx={{ letterSpacing: '-0.5px', textTransform: 'uppercase', lineHeight: 1.1 }}>
                Mohan Mali International School
              </Typography>
              <Stack direction="row" spacing={2} mt={1} color="text.secondary" flexWrap="wrap">
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', fontSize: '11.5px' }}>
                  <LocationOn sx={{ fontSize: 14, mr: 0.5, color: 'primary.main' }} /> At Alkud, Kavathe Mahankal, Sangli
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', fontSize: '11.5px' }}>
                  <Call sx={{ fontSize: 14, mr: 0.5, color: 'primary.main' }} /> +91 9890555478
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Box sx={{ display: 'inline-block', bgcolor: '#f1f5f9', px: 2, py: 1, borderRadius: 2, textAlign: 'right' }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', display: 'block', fontSize: '10px' }}>
                  {new Date(selectedDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' })}
                </Typography>
                <Typography variant="subtitle2" fontWeight={800} color="primary.dark">DAYBOOK STATEMENT</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress size={32} /></Box>
        ) : (
          <Box>
            {/* 1. OPENING LIQUIDITY */}
            <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1} border="1px dashed #cbd5e1" borderRadius={2} mb={2.5}>
              <Typography variant="body2" fontWeight={700} color="text.secondary">A. Opening Liquidity Balance Cash-In-Hand</Typography>
              <Typography variant="body2" fontWeight={800} color="text.primary">₹{openingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
            </Box>

            {/* 2. CREDITS TABLE (ALL INCOMES INCLUDED WALLET) */}
            <Typography variant="caption" fontWeight={800} color="primary.main" sx={{ display: 'block', textTransform: 'uppercase', mb: 0.8, letterSpacing: 0.5 }}>
              B. Cash / Online Incomes Today
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', py: 1, width: '60px' }}>Sr. No.</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Category Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Account Holder / Payer Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Particulars Remarks</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Method</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Amount Collected</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allIncomes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 2, fontSize: '12px', color: 'text.secondary', fontStyle: 'italic' }}>
                        No revenue receipts recorded for this date query.
                      </TableCell>
                    </TableRow>
                  ) : (
                    allIncomes.map((item, index) => (
                      <TableRow key={item.id}>
                        <TableCell sx={{ py: 0.8, fontSize: '12px', color: 'text.secondary' }}>{index + 1}</TableCell>
                        <TableCell sx={{
                          py: 0.8,
                          fontSize: '12px',
                          color: item.cat === "Fee Payment" ? "primary.dark" : item.cat === "Inventory" ? "warning.dark" : item.cat === "Wallet Deposit" ? "info.dark" : "secondary.dark",
                          fontWeight: 600
                        }}>
                          {item.cat}
                        </TableCell>
                        <TableCell sx={{ py: 0.8, fontSize: '12px', fontWeight: 700 }}>{item.name}</TableCell>
                        <TableCell sx={{ py: 0.8, fontSize: '12px', color: 'text.secondary' }}>{item.desc}</TableCell>
                        <TableCell sx={{ py: 0.8, fontSize: '12px' }}>{item.mode}</TableCell>
                        <TableCell align="right" sx={{ py: 0.8, fontSize: '12px', fontWeight: 700, color: 'success.main' }}>
                          ₹{item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  <TableRow sx={{ bgcolor: "#fdfdfd" }}>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 800, py: 1, fontSize: '12px' }}>Total Inward Credit Revenue (B):</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, py: 1, fontSize: '12px', color: 'success.dark', textDecorationLine: 'underline' }}>
                      ₹{dayTotalIncome.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* 3. DEBITS SECTION TABLE (EXPENSES) */}
            <Typography variant="caption" fontWeight={800} color="error.main" sx={{ display: 'block', textTransform: 'uppercase', mb: 0.8, letterSpacing: 0.5 }}>
              C. Cash / Online Expenses Today
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 4, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', py: 1, width: '60px' }}>Sr. No.</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Payment Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Paid Out To recipient</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Voucher Description / Note Particulars</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Amount Debited</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expenses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 2, fontSize: '12px', color: 'text.secondary', fontStyle: 'italic' }}>
                        No debit expenditure vouchers logged today.
                      </TableCell>
                    </TableRow>
                  ) : (
                    expenses.map((item, index) => (
                      <TableRow key={item.id}>
                        <TableCell sx={{ py: 0.8, fontSize: '12px', color: 'text.secondary' }}>{index + 1}</TableCell>
                        <TableCell sx={{ py: 0.8, fontSize: '12px', color: 'error.dark', fontWeight: 600 }}>{item.payMode} </TableCell>
                        <TableCell sx={{ py: 0.8, fontSize: '12px', fontWeight: 700 }}>{item.name}</TableCell>
                        <TableCell sx={{ py: 0.8, fontSize: '12px', color: 'text.secondary' }}>{item.desc}</TableCell>
                        <TableCell align="right" sx={{ py: 0.8, fontSize: '12px', fontWeight: 700, color: 'error.main' }}>
                          ₹{item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  <TableRow sx={{ bgcolor: "#fdfdfd" }}>
                    <TableCell colSpan={4} align="right" sx={{ fontWeight: 800, py: 1, fontSize: '12px' }}>Total Outward Debits (C):</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, py: 1, fontSize: '12px', color: 'error.dark', textDecorationLine: 'underline' }}>
                      ₹{dayTotalExpense.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* 4. NET CLOSING CALCULATION BOX */}
            <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 3, border: "1px solid #e2e8f0", marginBottom: 4 }}>
              <Grid container spacing={1} justifyContent="flex-end">
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Box display="flex" justifyContent="space-between" sx={{ fontSize: '12.5px' }}>
                      <Typography color="text.secondary">Opening Fluid Balance Base (A):</Typography>
                      <Typography fontWeight={600}>₹{openingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" sx={{ fontSize: '12.5px' }}>
                      <Typography color="text.secondary">Add: Today's Receipts Total (B):</Typography>
                      <Typography fontWeight={600} color="success.dark">+ ₹{dayTotalIncome.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" sx={{ fontSize: '12.5px' }}>
                      <Typography color="text.secondary">Less: Today's Expense Total (C):</Typography>
                      <Typography fontWeight={600} color="error.dark">- ₹{dayTotalExpense.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ pt: 0.5 }}>
                      <Typography fontWeight={800} color="text.primary" sx={{ fontSize: '13.5px' }}>Net Closing Hand Balance:</Typography>
                      <Typography variant="subtitle1" fontWeight={900} color="primary.dark">
                        ₹{closingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            {/* 5. BANKING TRANSACTIONS */}
            <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', mb: 0.8, letterSpacing: 0.5 }}>
              Separate Banking Ledger Rows (Excluded from above Cash Book)
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 2, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', py: 1, width: '60px' }}>Sr. No.</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Paid Out To recipient</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Voucher Description / Note Particulars</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Amount Debited</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expenses1.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 2, fontSize: '12px', color: 'text.secondary', fontStyle: 'italic' }}>
                        No secondary banking transaction vouchers logged today.
                      </TableCell>
                    </TableRow>
                  ) : (
                    expenses1.map((item, index) => (
                      <TableRow key={item.id}>
                        <TableCell sx={{ py: 0.8, fontSize: '12px', color: 'text.secondary' }}>{index + 1}</TableCell>
                        <TableCell sx={{ py: 0.8, fontSize: '12px', fontWeight: 700 }}>{item.name}</TableCell>
                        <TableCell sx={{ py: 0.8, fontSize: '12px', color: 'text.secondary' }}>{item.desc}</TableCell>
                        <TableCell align="right" sx={{ py: 0.8, fontSize: '12px', fontWeight: 700, color: 'error.main' }}>
                          ₹{item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mb: 2 }}>
              {dailyNotes.length === 0 ? (
                null
              ) : (
                <Grid container className="row" spacing={1.5}>
                  <Grid className="col-sm-12 " item xs={12} sm={6} md={4} >
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        height: "100%",
                        transition: "0.2s",
                        "&:hover": {
                          boxShadow: 2
                        }
                      }}
                    >
                      <Typography>Note : </Typography>
                      {dailyNotes.map((item, index) => (
                        <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }} >{item.description} </Typography>
                      ))}
                    </Paper>
                  </Grid>

                </Grid>
              )}
            </Box>


            <Box sx={{ mb: 2 }}>
              {bankingRecords.length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    textAlign: "center",
                    color: "text.secondary",
                    fontStyle: "italic"
                  }}
                >
                  No secondary banking transaction vouchers logged today.
                </Paper>
              ) : (
                <Grid container spacing={1.5}>
                  {bankingRecords.map((item, index) => (
                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          height: "100%",
                          transition: "0.2s",
                          "&:hover": {
                            boxShadow: 2
                          }
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ mt: 0.5 }}
                        >
                          {item.fieldName}
                        </Typography>

                        <Divider sx={{ my: 1 }} />

                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >

                          <Typography
                            variant="subtitle2"
                            fontWeight={800}
                            color={
                              item.fieldType?.toLowerCase() === "credit"
                                ? "success.dark"
                                : "error.dark"
                            }
                          >
                            ₹
                            {item.amount.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>

            {/* 6. SIGNATURE BLOCK */}
            <Box sx={{ mt: 8, display: 'block' }}>
              <Grid container justifyContent="space-between">
                <Grid item xs={3.5} textAlign="center">
                  <Box sx={{ borderTop: '1px solid #cbd5e1', pt: 0.8 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', display: 'block', fontSize: '10px' }}>Prepared By</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3.5} textAlign="center">
                  <Box sx={{ borderTop: '1px solid #cbd5e1', pt: 0.8 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', display: 'block', fontSize: '10px' }}>Audited / Checked By</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3.5} textAlign="center">
                  <Box sx={{ borderTop: '1px solid #cbd5e1', pt: 0.8 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', display: 'block', fontSize: '10px' }}>Authorized Signatory</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

          </Box>
        )}
      </Card>
    </Box>
  );
}

export default Daybook;